import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

import { API_URL } from "../lib/utils";

const AppContext = createContext(null);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [settings, setSettings] = useState({
    app_name: "Gestionale HR",
    logo_url: null,
    primary_color: "#000000"
  });
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/companies`, { withCredentials: true });
      setCompanies(response.data);
    } catch (err) {
      console.error("Error fetching companies:", err);
    }
  }, []);

  const fetchLocations = useCallback(async (companyId = null) => {
    try {
      const params = companyId ? { company_id: companyId } : {};
      const response = await axios.get(`${API_URL}/api/locations`, { params, withCredentials: true });
      setLocations(response.data);
    } catch (err) {
      console.error("Error fetching locations:", err);
    }
  }, []);

  const fetchEmployees = useCallback(async (companyId = null, locationId = null, search = null) => {
    try {
      const params = {};
      if (companyId) params.company_id = companyId;
      if (locationId) params.location_id = locationId;
      if (search) params.search = search;
      const response = await axios.get(`${API_URL}/api/employees`, { params, withCredentials: true });
      setEmployees(response.data);
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings`, { withCredentials: true });
      if (response.data) {
        setSettings(response.data);
      }
    } catch (err) {
      console.error("Error fetching settings:", err);
    }
  }, []);

  const updateSettings = async (newSettings) => {
    try {
      const response = await axios.put(`${API_URL}/api/settings`, newSettings, { withCredentials: true });
      setSettings(response.data);
      return response.data;
    } catch (err) {
      throw err;
    }
  };

  const seedData = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/seed`, {}, { withCredentials: true });
      await fetchCompanies();
      await fetchLocations();
      await fetchEmployees();
      return response.data;
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Dynamic favicon from logo
  useEffect(() => {
    if (settings.logo_url) {
      const logoUrl = settings.logo_url.startsWith("http")
        ? settings.logo_url
        : `${API_URL}${settings.logo_url}`;
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = logoUrl;
    }
  }, [settings.logo_url]);

  // Dynamic primary color as CSS variable
  useEffect(() => {
    if (settings.primary_color) {
      document.documentElement.style.setProperty("--primary-color", settings.primary_color);
      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta) meta.setAttribute("content", settings.primary_color);
    }
  }, [settings.primary_color]);

  const { user, isAdmin } = useAuth();

  // Filter companies and locations based on user's allowed_companies and allowed_locations
  const filteredCompanies = useMemo(() => {
    if (isAdmin || !user) return companies;
    const allowed = user.allowed_companies || [];
    if (allowed.length === 0) return companies;
    return companies.filter(c => allowed.includes(c.id));
  }, [companies, user, isAdmin]);

  const filteredLocations = useMemo(() => {
    if (isAdmin || !user) return locations;
    const allowed = user.allowed_locations || [];
    if (allowed.length === 0) return locations;
    return locations.filter(l => allowed.includes(l.id));
  }, [locations, user, isAdmin]);

  const value = {
    companies: filteredCompanies,
    locations: filteredLocations,
    allCompanies: companies,
    allLocations: locations,
    employees,
    settings,
    selectedCompany,
    selectedLocation,
    loading,
    setSelectedCompany,
    setSelectedLocation,
    fetchCompanies,
    fetchLocations,
    fetchEmployees,
    fetchSettings,
    updateSettings,
    seedData
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
